// Contribution from Mercatura DeFi Team to Menese Protocol
// (2-of-2 Threshold ECDSA — Tier 7 §3.F typed canister wrapper).
// Mercatura Forum proprietary.
//
// `MeneseCanister` is the typed surface the UI uses. Wraps
// `@dfinity/agent` Actor calls into TS-level Reply variants.
//
// Anti-stub posture (Tier-7 §1.B): the constructor REFUSES anonymous
// identity in production builds. Tests + local dev opt out via the
// `allowAnonymousForLocalDev` flag.

import { Actor, HttpAgent, type Identity } from "@dfinity/agent";
import { Principal } from "@dfinity/principal";

import { idlFactory } from "./idl";
import type {
  CanisterStatusReply,
  CombinedPublicKeyReply,
  EncryptedBlob,
  FetchEncryptedBlobReply,
  FetchNimPresigReply,
  NimPoolStatusReply,
  RegisterUserPubkeyArgs,
  RegisterUserPubkeyReply,
  SignViaNimReply,
} from "./types";

interface CanisterActor {
  register_user_pubkey: (args: RegisterUserPubkeyArgs) => Promise<RegisterUserPubkeyReply>;
  update_encrypted_blob: (blob: EncryptedBlob) => Promise<RegisterUserPubkeyReply>;
  fetch_encrypted_blob: () => Promise<FetchEncryptedBlobReply>;
  combined_public_key: () => Promise<CombinedPublicKeyReply>;
  canister_production_cl_crs: () => Promise<Uint8Array>;
  fetch_nim_presig: () => Promise<FetchNimPresigReply>;
  sign_via_nim: (request_bytes: Uint8Array) => Promise<SignViaNimReply>;
  fill_nim_pool: (target: number) => Promise<NimPoolStatusReply>;
  nim_pool_status: () => Promise<NimPoolStatusReply>;
  canister_status: () => Promise<CanisterStatusReply>;
}

export interface MeneseCanisterOptions {
  canisterId: string;
  identity: Identity;
  /** Replica URL. Production builds always go through `https://icp-api.io`
   *  or the canister's `https://<id>.icp0.io` boundary; local dev uses
   *  `http://127.0.0.1:4945`. */
  replicaUrl: string;
  /** Local dev / test flag — DEFAULT FALSE. Setting this to true is
   *  required to allow an anonymous identity into the constructor;
   *  production builds refuse. */
  allowAnonymousForLocalDev?: boolean;
}

export class MeneseCanister {
  readonly canisterId: Principal;
  private readonly actor: CanisterActor;

  constructor(opts: MeneseCanisterOptions) {
    if (
      opts.identity.getPrincipal().isAnonymous() &&
      !opts.allowAnonymousForLocalDev
    ) {
      throw new Error(
        "MeneseCanister: anonymous identity rejected (Tier-7 §1.B). " +
          "Log in via Internet Identity first, or pass " +
          "`allowAnonymousForLocalDev: true` for explicit local dev only.",
      );
    }
    if (
      opts.allowAnonymousForLocalDev &&
      // PROD env is set by Vite at build time; refuse the dev flag in production.
      typeof import.meta !== "undefined" &&
      (import.meta as { env?: { PROD?: boolean } }).env?.PROD === true
    ) {
      throw new Error(
        "MeneseCanister: allowAnonymousForLocalDev rejected in production build (§1.B)",
      );
    }
    this.canisterId = Principal.fromText(opts.canisterId);

    const agent = new HttpAgent({
      identity: opts.identity,
      host: opts.replicaUrl,
    });
    // On the production IC the agent fetches the root key from the
    // boundary node automatically; locally we need to opt in.
    if (!opts.replicaUrl.includes("icp-api.io") && !opts.replicaUrl.includes("icp0.io")) {
      void agent.fetchRootKey();
    }
    this.actor = Actor.createActor<CanisterActor>(idlFactory, {
      agent,
      canisterId: this.canisterId,
    });
  }

  registerUserPubkey(args: RegisterUserPubkeyArgs): Promise<RegisterUserPubkeyReply> {
    return this.actor.register_user_pubkey(args);
  }
  updateEncryptedBlob(blob: EncryptedBlob): Promise<RegisterUserPubkeyReply> {
    return this.actor.update_encrypted_blob(blob);
  }
  fetchEncryptedBlob(): Promise<FetchEncryptedBlobReply> {
    return this.actor.fetch_encrypted_blob();
  }
  combinedPublicKey(): Promise<CombinedPublicKeyReply> {
    return this.actor.combined_public_key();
  }
  canisterProductionClCrs(): Promise<Uint8Array> {
    return this.actor.canister_production_cl_crs();
  }
  fetchNimPresig(): Promise<FetchNimPresigReply> {
    return this.actor.fetch_nim_presig();
  }
  signViaNim(requestBytes: Uint8Array): Promise<SignViaNimReply> {
    return this.actor.sign_via_nim(requestBytes);
  }
  fillNimPool(target: number): Promise<NimPoolStatusReply> {
    return this.actor.fill_nim_pool(target);
  }
  nimPoolStatus(): Promise<NimPoolStatusReply> {
    return this.actor.nim_pool_status();
  }
  canisterStatus(): Promise<CanisterStatusReply> {
    return this.actor.canister_status();
  }
}
